import type { DisplayEvent, EventSeverity } from '@/types/api'

/** Player telemetry events (PLAYER_PROTOCOL.md §14). */

export const SEVERITIES: readonly EventSeverity[] = ['debug', 'info', 'warning', 'error', 'critical']

export const SEVERITY_META: Record<EventSeverity, { label: string; tone: 'muted' | 'info' | 'warning' | 'danger' | 'critical' }> = {
  debug: { label: 'Debug', tone: 'muted' },
  info: { label: 'Info', tone: 'info' },
  warning: { label: 'Warning', tone: 'warning' },
  error: { label: 'Error', tone: 'danger' },
  critical: { label: 'Critical', tone: 'critical' },
}

/** Severity filter presets — the timeline defaults to `warning` and above. */
export type SeverityFilter = 'all' | 'info' | 'warning' | 'error'

export const SEVERITY_FILTERS: { value: SeverityFilter; label: string }[] = [
  { value: 'warning', label: 'Warnings & errors' },
  { value: 'error', label: 'Errors only' },
  { value: 'info', label: 'Info and above' },
  { value: 'all', label: 'Everything (incl. debug)' },
]

export const DEFAULT_SEVERITY_FILTER: SeverityFilter = 'warning'

/** Severities sent as `severity[]`; `undefined` = no filter. */
export function severitiesFor(filter: SeverityFilter): EventSeverity[] | undefined {
  if (filter === 'all') return undefined
  return SEVERITIES.slice(SEVERITIES.indexOf(filter))
}

export function severityMeta(severity: string) {
  return (SEVERITY_META as Record<string, (typeof SEVERITY_META)[EventSeverity]>)[severity] ?? { label: severity, tone: 'muted' as const }
}

/** Human titles for known `PlayerEventType`s. Unknown types are shown verbatim. */
export const EVENT_TITLES: Record<string, string> = {
  boot: 'Device booted',
  service_started: 'Player service started',
  sync_applied: 'Content sync applied',
  sync_failed: 'Content sync failed',
  download_failed: 'Download failed',
  checksum_mismatch: 'Checksum mismatch',
  activation_failed: 'Content activation failed',
  renderer_started: 'Renderer started',
  renderer_crash: 'Renderer crashed',
  renderer_restart: 'Renderer restarted',
  renderer_recycled: 'Renderer recycled',
  safe_mode_entered: 'Entered safe mode',
  safe_mode_exited: 'Left safe mode',
  auto_rollback: 'Automatic rollback',
  unknown_element_type: 'Unknown element type',
  unsupported_content: 'Unsupported content',
  clock_jump: 'Clock jumped',
  clock_skew: 'Clock skew detected',
  storage_low: 'Storage low',
  gc_completed: 'Cache cleanup finished',
  schedule_stale: 'Schedule is stale',
  emergency_started: 'Emergency broadcast started',
  emergency_ended: 'Emergency broadcast ended',
  update_downloaded: 'Player update downloaded',
  update_installed: 'Player update installed',
  update_committed: 'Player update committed',
  update_rolled_back: 'Player update rolled back',
  credential_rotated: 'Credentials rotated',
  network_changed: 'Network changed',
  display_connected: 'Screen connected',
  display_disconnected: 'Screen disconnected',
  data_source_failed: 'Data source failed',
  command_failed: 'Command failed',
}

export function eventTitle(type: string): string {
  return EVENT_TITLES[type] ?? type
}

export function isKnownEventType(type: string): boolean {
  return Object.hasOwn(EVENT_TITLES, type)
}

/** Events that mean the screen is not playing what was published — highlighted prominently. */
export const HIGHLIGHT_EVENT_TYPES: readonly string[] = ['auto_rollback', 'safe_mode_entered']

export function isHighlightEvent(e: Pick<DisplayEvent, 'type'>): boolean {
  return HIGHLIGHT_EVENT_TYPES.includes(e.type)
}

/**
 * The most recent highlight event among `events` (newest first) that is still relevant:
 * within `windowMs` and — for safe mode — not followed by `safe_mode_exited`.
 */
export function activeHighlight(events: readonly DisplayEvent[], now: Date = new Date(), windowMs = 24 * 3_600_000): DisplayEvent | null {
  for (const e of events) {
    const at = new Date(e.occurred_at).getTime()
    if (Number.isNaN(at) || now.getTime() - at > windowMs) return null
    if (e.type === 'safe_mode_exited') return null
    if (isHighlightEvent(e)) return e
  }
  return null
}

export interface EventsCursor {
  before: string
  before_id: string
}

/** Keyset cursor for the next (older) page: the last row's `(occurred_at, id)` of a full page, else `undefined` (no more). */
export function nextBeforeCursor(page: readonly Pick<DisplayEvent, 'occurred_at' | 'id'>[], limit: number): EventsCursor | undefined {
  if (page.length < limit || page.length === 0) return undefined
  const last = page[page.length - 1]
  return { before: last.occurred_at, before_id: last.id }
}

/** Drops duplicates across pages (same id) while keeping order. */
export function mergeEventPages(pages: readonly (readonly DisplayEvent[])[]): DisplayEvent[] {
  const seen = new Set<string>()
  const out: DisplayEvent[] = []
  for (const page of pages)
    for (const e of page) {
      if (seen.has(e.id)) continue
      seen.add(e.id)
      out.push(e)
    }
  return out
}
